import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingCard,
  Skeleton,
} from '@folio/components';
import { useInvoicingFrequencyLabels } from '@folio/constants';
import { cn, formatNumber } from '@folio/lib';
import { useKorintProductConfig } from 'korint-sdk/react';
import { AlertCircleIcon, CircleCheckBig, CircleX, MinusIcon } from 'lucide-react';
import { groupBy } from 'lodash';
import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CustomIncludedPerilsProps } from 'korint-sdk';
import { QuoteContext } from '../shared-quote/QuoteContext';
import { type QuoteError, quoteFailedUserHelp } from '../../utils/quoteFailedHelp';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Alert, AlertDescription } from '@folio/components/ui/alert';

function IncludedPerilsContent({
  customIncludedPerils,
  tier,
  includedPerils,
  optionalPerils,
  productId,
  isGuaranteeExcessDisplayed,
}: {
  customIncludedPerils?: (props: CustomIncludedPerilsProps) => React.ReactNode;
  tier: string;
  includedPerils: Array<{ id: string; unavailabilityMessage?: string }>;
  optionalPerils: Array<{ id: string; context: string }>;
  productId: string;
  isGuaranteeExcessDisplayed: boolean;
}) {
  const { t } = useTranslation();

  if (customIncludedPerils) {
    return <>{customIncludedPerils({ tier, includedPerils })}</>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {includedPerils.map(p => {
        const isAvailable = !p.unavailabilityMessage;
        const emoji = t(`${productId}.perils.${p.id}.emoji`, { defaultValue: '' });
        const excess = isGuaranteeExcessDisplayed
          ? t(`${productId}.perils.${p.id}.excess`)
          : undefined;
        return (
          <li
            key={p.id}
            className={cn(
              'flex items-start gap-2 text-sm',
              !isAvailable && 'text-muted-foreground',
            )}
          >
            {isAvailable ? (
              emoji ? (
                <span className="shrink-0 mt-0.5" aria-hidden="true">
                  {emoji}
                </span>
              ) : (
                <CircleCheckBig className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
              )
            ) : (
              <CircleX className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground/50" />
            )}
            <Stack gap={0}>
              <span className={cn(!isAvailable && 'line-through')}>
                {t(`${productId}.perils.${p.id}.label`)}
                {excess && (
                  <Text inline tone="muted">
                    {' '}
                    ({excess})
                  </Text>
                )}
              </span>
              {!isAvailable && (
                <Text inline size="xs" className="italic">
                  {p.unavailabilityMessage}
                </Text>
              )}
            </Stack>
          </li>
        );
      })}
      {optionalPerils.map(p => (
        <li key={p.id} className="flex items-start gap-2 text-sm text-muted-foreground">
          <MinusIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            {t(`${productId}.perils.${p.id}.tierLabel`, {
              context: p.context,
              defaultValue: t(`${productId}.perils.${p.id}.label`),
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function QuoteTierSelectModule() {
  const { t } = useTranslation();
  const invoicingFrequencyLabels = useInvoicingFrequencyLabels();

  const form = useFormContext();

  const { policy, quoteConfig, calculatedQuotes, isLoading } = useContext(QuoteContext);

  const { productsConfig } = useKorintProductConfig();

  const { quoteTierSelectModuleConfig } = productsConfig?.[policy?.productId!] || {};

  const currentInsurer = form.watch('insurer');
  const currentTier = form.watch('tier');
  const currentExcess = form.watch('excess');

  const invoicingFrequency =
    form.watch('invoicingConfig.frequency') ?? policy?.invoicingConfig?.frequency;

  useEffect(() => {
    const isCurrentQuoteAvailable = calculatedQuotes.some(
      ({ insurer, tier, quote }) =>
        quote?.policy?.totalPremium.amountWithFeesAndTaxes !== undefined &&
        tier === currentTier &&
        insurer === currentInsurer,
    );

    if (currentTier && !isCurrentQuoteAvailable) {
      const firstAvailableQuote = calculatedQuotes.find(
        ({ quote }) => quote?.policy?.totalPremium.amountWithFeesAndTaxes !== undefined,
      );

      if (firstAvailableQuote) {
        const { insurer, tier, excess } = firstAvailableQuote;

        form.setValue('insurer', insurer, { shouldDirty: true, shouldTouch: true });
        form.setValue('tier', tier, { shouldDirty: true, shouldTouch: true });
        form.setValue('excess', excess, { shouldDirty: true, shouldTouch: true });
      }
    }
  }, [currentInsurer, currentTier, calculatedQuotes]);

  if (isLoading) {
    return <LoadingCard />;
  }

  const availableQuotes = calculatedQuotes.filter(
    ({ quote }) => quote?.policy?.totalPremium.amountWithFeesAndTaxes !== undefined,
  );
  const quotesByInsurer = Object.entries(groupBy(availableQuotes, ({ insurer }) => insurer ?? ''));

  // Every tier hits the same insurer with the same inputs, so a failure repeats across all of them.
  const failedQuoteErrors = [
    ...new Map(
      calculatedQuotes
        .flatMap(
          ({ error }) => (error?.details as { quoteErrors?: QuoteError[] })?.quoteErrors ?? [],
        )
        .map(quoteError => [quoteError.error, quoteError]),
    ).values(),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div>Choisissez votre formule</div>
          <div className="text-sm text-gray-600">
            Sélectionnez la formule qui correspond le mieux à vos besoins.
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {availableQuotes.length === 0 && (
          <Alert variant="warning">
            <AlertCircleIcon />
            <AlertDescription>
              <Stack gap={1}>
                <p>Aucune formule disponible. Veuillez vérifier les données saisies.</p>
                {failedQuoteErrors.length > 0 && <p>{quoteFailedUserHelp(failedQuoteErrors)}</p>}
              </Stack>
            </AlertDescription>
          </Alert>
        )}
        <Stack gap={6}>
          {quotesByInsurer.map(([insurerGroup, insurerQuotes]) => (
            <Stack key={insurerGroup} gap={3}>
              {insurerGroup && <Text weight="bold">{t(`insurer.${insurerGroup}`)}</Text>}
              <div
                className={cn(
                  'grid gap-4',
                  // Tailwind needs to be explicit about the classnames, it cannot be dynamic
                  // https://tailwindcss.com/docs/detecting-classes-in-source-files
                  // Max 3 formulas per row — 4 formulas → 2×2 grid
                  insurerQuotes.length === 1 && 'grid-cols-1',
                  insurerQuotes.length === 2 && 'md:grid-cols-2',
                  insurerQuotes.length === 3 && 'md:grid-cols-2 lg:grid-cols-3',
                  insurerQuotes.length === 4 && 'md:grid-cols-2',
                  insurerQuotes.length > 4 && 'md:grid-cols-2 lg:grid-cols-3',
                )}
              >
                {insurerQuotes.map(({ insurer, tier, quote, excess, breakdown }) => {
                  const recurringFromComponents = quote?.policy?.components
                    ?.filter(c => c.chargeType === 'RECURRING')
                    .reduce((sum, c) => sum + (c.premium?.amountWithFeesAndTaxes ?? 0), 0);
                  const perils = quote?.assets?.[0]?.options.perils;

                  const tierPerils = quoteConfig?.availableConfigs.find(
                    c => c.tier === tier,
                  )?.perils;

                  const includedPerils =
                    tierPerils
                      ?.filter(p => p.required && !p.options)
                      ?.map(p => {
                        const perilFromQuote = perils?.find(pp => pp.id === p.id);
                        return {
                          id: p.id,
                          unavailabilityMessage: perilFromQuote?.unavailabilityMessage,
                        };
                      }) ?? [];

                  const configuredOptionalPerilIds =
                    quoteTierSelectModuleConfig?.optionalPerilIds ?? [];
                  const optionalPerils =
                    tierPerils
                      ?.filter(
                        p => !p.required && !p.options && configuredOptionalPerilIds.includes(p.id),
                      )
                      ?.map(p => {
                        const perilFromQuote = perils?.find(pp => pp.id === p.id);
                        const isActive = !!perilFromQuote && !perilFromQuote.unavailabilityMessage;
                        return { id: p.id, context: String(isActive) };
                      }) ?? [];

                  const total = breakdown?.recurring?.withFeesAndTaxes ?? recurringFromComponents;

                  const isSelected =
                    currentInsurer === insurer && currentTier === tier && currentExcess === excess;

                  const tierLabel = t([
                    `${policy?.productId}.tiers.${tier}.label`,
                    `tiers.${tier}.label`,
                  ]);
                  const tierDescription = t([
                    `${policy?.productId}.tiers.${tier}.description`,
                    `tiers.${tier}.description`,
                    'empty',
                  ]);

                  return (
                    <Stack
                      key={`${insurer}-${tier}`}
                      className={cn(
                        'rounded-2xl border-2 p-6 gap-5 transition-all duration-200',
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-primary/40 hover:shadow-sm',
                      )}
                    >
                      <Stack gap={1}>
                        <div className="text-lg font-bold">{tierLabel}</div>
                        {tierDescription && (
                          <div className="text-sm text-muted-foreground">{tierDescription}</div>
                        )}
                      </Stack>

                      <Group align="baseline" gap={0} className="gap-1.5">
                        <Text inline weight="bold" className="text-4xl">
                          {total ? (
                            `${formatNumber(total / 100)} €`
                          ) : (
                            <Skeleton className="w-24 h-9 inline-flex" />
                          )}
                        </Text>
                        {invoicingFrequency in invoicingFrequencyLabels && (
                          <Text inline size="sm" tone="muted">
                            / {invoicingFrequencyLabels[invoicingFrequency]}
                          </Text>
                        )}
                      </Group>

                      <div
                        className={cn(
                          'grow rounded-xl p-3 transition-colors duration-200',
                          isSelected ? 'bg-primary/10' : 'bg-muted/50',
                        )}
                      >
                        <IncludedPerilsContent
                          customIncludedPerils={quoteTierSelectModuleConfig?.customIncludedPerils}
                          tier={tier}
                          includedPerils={includedPerils}
                          optionalPerils={optionalPerils}
                          productId={policy?.productId!}
                          isGuaranteeExcessDisplayed={
                            productsConfig?.[policy?.productId!]?.isGuaranteeExcessDisplayed ??
                            false
                          }
                        />
                      </div>

                      <Button
                        variant={isSelected ? 'default' : 'outline'}
                        className="w-full"
                        onClick={() => {
                          if (!isSelected) {
                            form.setValue('insurer', insurer, { shouldDirty: true });
                            form.setValue('tier', tier, { shouldDirty: true });
                            form.setValue('excess', excess, { shouldDirty: true });
                          }
                        }}
                      >
                        {isSelected ? 'Formule sélectionnée' : 'Choisir cette formule'}
                      </Button>
                    </Stack>
                  );
                })}
              </div>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
